import type { audit as AuditNS } from '@cwos/api-client';

/**
 * Pure presentation logic for the Audit + Compliance Center.
 *
 * The server already decided WHAT a viewer may be told — this file only decides
 * how it reads. Keeping that split means the rules that matter (redaction,
 * authorization) are tested against the database, and the rules that are merely
 * cosmetic are tested here, without a browser.
 */

// --- Time ------------------------------------------------------------------

export type RangePreset = 'today' | '7d' | '30d' | '90d' | 'all' | 'custom';

export const RANGE_LABELS: Record<RangePreset, string> = {
  today: 'Today',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  all: 'All recorded activity',
  custom: 'Custom range',
};

/**
 * A `YYYY-MM-DD` picked on a date input, as the reader's LOCAL midnight.
 *
 * The picker hands over a calendar day with no zone attached; the screen's own
 * day headings (`dayHeading`) name days by the DEVICE's calendar. Building the
 * boundary with `T00:00:00.000Z` instead treats the picked day as a UTC day,
 * which shifts every boundary by the viewer's offset — events from 5 pm on the
 * previous day slide into a range the reader picked as starting today, and the
 * last five hours of the picked end day fall out of it. Local-midnight math
 * keeps the range and the headings on the same calendar.
 */
function localMidnight(day: string): Date {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

/**
 * Resolves a preset to an absolute window.
 *
 * `now` is a parameter rather than a call to `Date.now()` so the boundaries are
 * testable, and so a range shown on screen is the same one the export receives.
 */
export function resolveRange(
  preset: RangePreset,
  now: Date,
  custom?: { from?: string; to?: string },
): { from?: string; to?: string } {
  if (preset === 'all') return {};
  if (preset === 'custom') {
    return {
      from: custom?.from ? localMidnight(custom.from).toISOString() : undefined,
      // THE API'S `to` IS EXCLUSIVE, AND THIS HAD FORGOTTEN IT. The picked end
      // day was sent as ITS OWN midnight, so `occurred_at < to` excluded the
      // entire final day of every custom range — from the log and the export
      // alike. `inclusiveEnd` existed and was tested; this is the call site
      // that never used it.
      to: custom?.to ? inclusiveEnd(custom.to) : undefined,
    };
  }
  if (preset === 'today') {
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    return { from: start.toISOString() };
  }
  const days = preset === '7d' ? 7 : preset === '30d' ? 30 : 90;
  return { from: new Date(now.getTime() - days * 86_400_000).toISOString() };
}

/** The end day a custom range must send so the picked day is included. */
export function inclusiveEnd(day: string): string {
  const start = localMidnight(day);
  return new Date(start.getFullYear(), start.getMonth(), start.getDate() + 1).toISOString();
}

/** "Aug 16, 2026 · 9:42 AM" — the row timestamp. */
export function formatEventTime(iso: string, locale?: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const date = d.toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' });
  const time = d.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });
  return `${date} · ${time}`;
}

/** Day heading used to group a page into "Today", "Yesterday", then dates. */
export function dayHeading(iso: string, now: Date, locale?: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const day = (x: Date) => `${x.getFullYear()}-${x.getMonth()}-${x.getDate()}`;
  // LOCAL-midnight yesterday, not `now - 86_400_000`: across a DST boundary
  // (a 23- or 25-hour day) subtracting raw milliseconds lands an hour off the
  // calendar, and an event an hour before midnight then reads as "today" or
  // skips "Yesterday" for a full day.
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  if (day(d) === day(now)) return 'Today';
  if (day(d) === day(yesterday)) return 'Yesterday';
  return d.toLocaleDateString(locale, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Groups a page into day sections, preserving the server's ordering. */
export function groupByDay(
  events: readonly AuditNS.AuditEvent[],
  now: Date,
  locale?: string,
): { heading: string; events: AuditNS.AuditEvent[] }[] {
  const out: { heading: string; events: AuditNS.AuditEvent[] }[] = [];
  for (const event of events) {
    const heading = dayHeading(event.occurredAt, now, locale);
    const last = out[out.length - 1];
    if (last && last.heading === heading) last.events.push(event);
    else out.push({ heading, events: [event] });
  }
  return out;
}

// --- Presentation ----------------------------------------------------------

export type Tone = 'plain' | 'sky' | 'urgent' | 'good';

export interface SensitivityBadge {
  variant: Tone;
  label: string;
  /** Why this row is marked, in words — never colour alone. */
  meaning: string;
}

/**
 * A privacy read and a security change are the two rows a reviewer is actually
 * hunting for, so both are labelled rather than left to blend into the list.
 */
export function sensitivityBadge(sensitivity: AuditNS.AuditSensitivity): SensitivityBadge | null {
  if (sensitivity === 'privacy_read') {
    return {
      variant: 'urgent',
      label: 'Sensitive view',
      meaning: 'Someone looked at protected information. Recorded on purpose.',
    };
  }
  if (sensitivity === 'security') {
    return {
      variant: 'sky',
      label: 'Access or security',
      meaning: 'This changed who can do what, or how the organization is protected.',
    };
  }
  return null;
}

/**
 * How an actor is described in the filter list and on a row.
 *
 * THIS SWITCH WAS WRONG, and the type is why. `ActorKind` in the API client
 * listed five members while `projectActor` had been emitting six — a
 * `'withheld'` actor has existed on the wire since the actor gate landed. The
 * `default` branch caught it and returned nothing, so the one kind of actor
 * that needed explaining was the one kind that got none.
 *
 * The `'removed'` line was worse than silent. It said "The account that did
 * this no longer exists, OR you cannot view members" — one sentence covering
 * two facts that are not the same and must not be run together. `'removed'`
 * means the account is GONE. `'withheld'` means it is there and you may not be
 * told whose it is. A compliance reader deciding whether a departed employee
 * did something cannot act on a sentence that means either.
 *
 * Written as an exhaustive switch over the corrected union with no `default`,
 * so the next kind the server invents fails to compile here instead of silently
 * rendering as nothing.
 */
export function actorHint(actor: AuditNS.AuditActor): string | null {
  switch (actor.kind) {
    case 'system':
      return 'An automated process, not a person.';
    case 'automation':
      return 'A rule your organization configured, running on its own.';
    case 'support':
      return 'Phibian support acting under a support grant.';
    case 'removed':
      return 'The account that did this no longer exists.';
    case 'withheld':
      return (
        'A member of your organization did this. Their name is withheld from you — ' +
        'naming a member needs the directory permission. This is not a removed account.'
      );
    case 'person':
      return null;
  }
}

/**
 * Why the "Person" filter has nothing in it.
 *
 * An empty `actors` list means one of two things and the screen must not
 * conflate them: the window genuinely holds no acted-on rows, or this viewer
 * lacks `user:read` and may not be handed a roster of members. `actorsWithheld`
 * is the server's answer, sent on every response in both states — and it was
 * missing from `AuditFilterOptions` in the API client, so nothing read it and
 * the select silently collapsed to its "Anyone" option. On a compliance screen
 * that reads as an organization where nobody has done anything.
 *
 * Null when the list is populated, and null when it is honestly empty: there is
 * nothing to explain in either case, and inventing a sentence for the second
 * would be the same error in the other direction.
 */
export function actorFilterNote(
  filters: Pick<AuditNS.AuditFilterOptions, 'actors' | 'actorsWithheld'> | undefined,
): string | null {
  if (!filters) return null;
  if (!filters.actorsWithheld) return null;
  return (
    'Filtering by person is not available to you — naming members of your organization ' +
    'needs the directory permission. Every row below is still here; the people who acted ' +
    'are shown as withheld.'
  );
}

/**
 * The line shown when a target could not be named.
 *
 * This is a genuine and deliberate outcome, not a loading failure: an audit row
 * never resolves an identifier into a label the viewer could not have obtained
 * directly, so it says so plainly rather than showing a blank.
 */
export function targetLine(target: AuditNS.AuditTarget): string {
  if (target.label) return target.label;
  return target.typeLabel;
}

/**
 * What a reader needs to know about the record this row points at: either why
 * it is not named, or — when it IS named — what that name does and does not
 * claim.
 *
 * THE UNNAMED HALF used to return the permission sentence for EVERY unnamed
 * target. Most automation rows have no name to show at all, so an org_admin
 * reading their own audit log was told, repeatedly, that they lacked permission
 * to see records nobody was withholding. Saying nothing is better than saying
 * something untrue about the reader's own access.
 *
 * THE NAMED HALF is the disclosure `target.labelResolvedAt` carries. The server
 * resolves a name by joining to that record's CURRENT row, so an article
 * renamed after an acknowledgement re-renders every historical row under
 * today's title. Until now that reached a customer only through the CSV
 * export's provenance block — the person reading the same event on screen was
 * shown a live-resolved name with nothing saying it was one, and a compliance
 * reader is exactly the audience who assumes otherwise.
 *
 * THE SENTENCE IS DELIBERATELY NARROW, and says the same thing as the server's
 * `CURRENT_LABEL_NOTICE` in the space one line allows. It qualifies the NAME
 * and nothing else, because the name is the only part that is a fact about
 * today: who acted, what they did, the record's id and the instant are
 * append-only and untouched, so the second clause states that rather than
 * leaving a reader to wonder how far the doubt reaches. It does not say the
 * record is unreliable, does not say the name PROBABLY changed, and does not
 * hedge the event — an audit log that talks itself down is worth less than one
 * that says precisely what it holds.
 *
 * `labelResolvedAt` is null exactly when NO NAME WAS SHOWN, and that is a
 * different statement, not a weaker one: a withheld row keeps the permission
 * sentence, and a row with nothing to name keeps its silence. Saying "the name
 * may have changed" over a row that never displayed a name would invent a
 * disclosure out of an absence.
 */
export function targetNote(target: AuditNS.AuditTarget): string | null {
  if (target.label) {
    // No instant, no claim about an instant. The server always sends one beside
    // a name it resolved; absent means nobody said, and nobody said is not
    // grounds for the sentence below.
    if (!target.labelResolvedAt) return null;
    const readAt = formatEventTime(target.labelResolvedAt);
    return (
      `This is the name the record has today${readAt ? `, read ${readAt}` : ''}, not the name ` +
      'it had at the time. What was recorded — who acted, what they did, and when — is unchanged.'
    );
  }
  return target.labelWithheld ? 'You do not have permission to see which record this was.' : null;
}

// --- Empty and error states -------------------------------------------------

export type EmptyReason = 'no_activity' | 'filtered_out';

export function emptyReason(query: {
  category?: string;
  action?: string;
  actorUserId?: string;
  from?: string;
  to?: string;
}): EmptyReason {
  const filtered = Boolean(
    query.category || query.action || query.actorUserId || query.from || query.to,
  );
  return filtered ? 'filtered_out' : 'no_activity';
}

// --- Narrowing to one kind of event ------------------------------------------

export type ActionOption = AuditNS.AuditFilterOptions['actions'][number];

/**
 * The actions worth offering, given the category already chosen.
 *
 * "People & access" is fourteen different events, and an operator asking "who
 * changed this person's role" has to read all fourteen to find the three that
 * matter. The API has always accepted an `action` filter and always narrowed
 * correctly; no screen offered it, because no endpoint said which actions
 * exist. This is the list, restricted to the chosen category so the dropdown
 * stays a short list rather than becoming the hundred-and-fifty-item one that
 * the category filter exists to avoid.
 *
 * Sorted by LABEL, not by key: the label is what a person reads, and sorting a
 * visible list by an invisible value looks like no sort at all.
 */
export function actionOptions(
  actions: readonly ActionOption[] | undefined,
  category: string,
): ActionOption[] {
  const scoped = category
    ? (actions ?? []).filter((a) => a.category === category)
    : (actions ?? []);
  return [...scoped].sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * The query an action choice produces.
 *
 * An action and a category are MUTUALLY EXCLUSIVE at the API — sending both is
 * a 400 — and sending both would be redundant anyway, since every action
 * belongs to exactly one category. So a chosen action replaces the category on
 * the wire while the category select keeps showing what the person picked.
 */
export function scopeQuery(
  category: string,
  action: string,
): { category?: string; action?: string } {
  if (action) return { action };
  if (category) return { category };
  return {};
}

export const EMPTY_COPY: Record<EmptyReason, { heading: string; message: string }> = {
  no_activity: {
    heading: 'No activity recorded yet',
    message:
      'Phibian records consequential actions — access changes, approvals, policy changes and views of sensitive data — as they happen. They will appear here.',
  },
  filtered_out: {
    heading: 'Nothing matches these filters',
    message: 'Widen the date range, or clear a filter, to see more activity.',
  },
};

// --- Export ----------------------------------------------------------------

/**
 * The sentence shown next to the export button.
 *
 * It states the scope the file will actually contain, because "Export" with no
 * statement of scope is how someone hands a regulator the wrong range.
 */
export function exportScopeLabel(
  preset: RangePreset,
  filters: { categoryLabel?: string; actorLabel?: string; actionLabel?: string },
): string {
  const parts = [RANGE_LABELS[preset].toLowerCase()];
  // The action is the narrower of the two, and when one is chosen it is the
  // scope the file will actually contain — naming the category as well would
  // overstate what is in it.
  if (filters.actionLabel) parts.push(`only "${filters.actionLabel.toLowerCase()}"`);
  else if (filters.categoryLabel) parts.push(filters.categoryLabel.toLowerCase());
  if (filters.actorLabel) parts.push(`actions by ${filters.actorLabel}`);
  return `Exports ${parts.join(', ')} — the same rows and the same columns you can see here.`;
}

// --- Payload sanity ---------------------------------------------------------

/**
 * Is this actually the payload it claims to be?
 *
 * The transport returns a non-JSON response as a raw string rather than
 * throwing, and the SPA host serves `index.html` for any path it does not
 * recognise. Put together, a client build that outlives its API — a bundle
 * cached across a rolling deploy, calling a path that has since moved — receives
 * an HTML page with a 200 and hands it back as "data". Dereferencing that gives
 * a white screen on the one surface whose job is to be dependable.
 *
 * So each screen checks the one field it immediately relies on, and falls back
 * to the error panel it already has. This is deliberately not a schema
 * validator: the API is trusted, and the failure being guarded is a version
 * skew, not a malformed domain response.
 */
export function looksLikePayload<T extends object>(data: unknown, key: keyof T): data is T {
  return typeof data === 'object' && data !== null && key in data;
}

// --- Compliance controls ----------------------------------------------------

export interface ControlPresentation {
  variant: Tone;
  label: string;
}

/**
 * How a control's observed state reads.
 *
 * Deliberately NOT pass/fail. "Not configured" is an observation about this
 * organization's settings, not a finding against it — an organization that does
 * not collect location has an unconfigured location policy on purpose.
 */
export function controlPresentation(state: AuditNS.ControlState): ControlPresentation {
  switch (state) {
    case 'configured':
      return { variant: 'good', label: 'In place' };
    case 'not_configured':
      return { variant: 'plain', label: 'Not set up' };
    case 'not_applicable':
      return { variant: 'plain', label: 'Nothing to show' };
  }
}

/**
 * Guard against a certification claim ever reaching a compliance surface.
 *
 * Phibian holds no SOC 2, HITRUST or ISO 27001 attestation. This predicate is
 * asserted in tests over every string the Compliance Center renders, so a
 * well-meaning copy edit cannot introduce one.
 *
 * NEGATION MATTERS. The most important sentence this product says about
 * certification is a denial — "Phibian does not hold SOC 2, HITRUST or ISO 27001
 * certification" — and a guard that flags its own honest disclaimer is a guard
 * someone deletes the first time it blocks them. So a framework named inside a
 * negated clause is not a claim.
 */
const CERTIFICATION_CLAIM =
  /\b(soc\s*2|soc2|hitrust|iso\s*?27001|pci[\s-]?dss)\b[^.]{0,40}?\b(compliant|certified|certification|accredited|attested)\b|\b(compliant|certified)\s+with\s+(soc|hitrust|iso)\b/gi;

/**
 * "does not", "no", "never", "nothing", "isn't", "without" — anywhere earlier in
 * the sentence.
 *
 * This list is not exhaustive English, and it does not need to be: an unlisted
 * negation makes the guard fire on honest copy, which costs a rewrite. The
 * opposite error ships a false certification claim. It errs that way on purpose.
 */
const NEGATION =
  /\b(not|no|never|none|nothing|neither|nor|without|cannot|lacks?|holds\s+no|doesn|isn|aren|won)\b|n['’]t\b/i;

export function makesCertificationClaim(text: string): boolean {
  CERTIFICATION_CLAIM.lastIndex = 0;
  for (const match of text.matchAll(CERTIFICATION_CLAIM)) {
    // The clause leading up to the claim, back to the previous sentence end.
    const before = text.slice(0, match.index);
    const sentence = before.slice(before.lastIndexOf('.') + 1);
    if (!NEGATION.test(sentence)) return true;
  }
  return false;
}

/* --- What an export that has ALREADY REACHED the browser says about itself -- */

/**
 * The one sentence a completed audit export earns, from three facts the server
 * sent about it, kept apart.
 *
 * FOUR OUTCOMES, NOT TWO. An export can be capped, it can be a measured empty,
 * it can be a measured N — and it can be a file whose row count the server did
 * not state, which is none of the other three. That last one is the whole
 * reason this is a function rather than a ternary at the render site: the
 * honest sentence there says the file was downloaded and says nothing about how
 * much is in it, because we do not know. Rendering "0 events" from an absent
 * `x-export-row-count` is the collapse this repository has now found at
 * nineteen sites — a claim about the organization's activity made out of a
 * missing header.
 *
 * CAPPED OUTRANKS THE COUNT. A capped export's row count is true and
 * misleading in the same breath: it is the number of rows in the FILE, not the
 * number of events in the range, and a compliance reader who takes it for the
 * second has a short evidence file they believe is complete. So the cap is said
 * first and the number is not offered beside it.
 *
 * This function is only ever called after `downloadTextFile` has returned. It
 * describes a file the operator has; it is not a description of a server
 * response. A failed export renders the failure instead and never reaches here.
 */
export function exportOutcome(result: { truncated: boolean; rowCount: number | null }): string {
  if (result.truncated) {
    return 'That export was capped at the maximum row count, so it is not the whole range — narrow the dates and export again for a complete file.';
  }
  if (result.rowCount === null) {
    return 'Export downloaded. Phibian did not state how many events it contains, so open the file to see.';
  }
  if (result.rowCount === 0) {
    return 'Export downloaded, and it contains no activity — nothing in your organization matched this scope.';
  }
  return `Export downloaded: ${result.rowCount} ${result.rowCount === 1 ? 'event' : 'events'}.`;
}
